const SVG_NS = "http://www.w3.org/2000/svg";

export function getAxisDistanceUnit(axis, units = {}) {
    return axis?.motionType === "linear" ? units.distanceLinear : (units.angleRotary || "deg");
}

export function getAxisAccelUnit(axis, units = {}) {
    return axis?.motionType === "linear" ? units.accelLinear : (units.accelRotary || "deg/s2");
}

export function getAxisSpeedUnit(axis, units = {}) {
    return axis?.motionType === "linear" ? units.speedLinear : (units.speedRotary || "deg/s");
}

export function applyTrackGridBackground(track, pxPerSec, deps = {}) {
    const { buildTrackGridStyle = () => ({}) } = deps;
    if (!track) return null;
    const style = buildTrackGridStyle(pxPerSec);
    track.style.backgroundImage = style.backgroundImage;
    track.style.backgroundSize = style.backgroundSize;
    track.style.backgroundPosition = style.backgroundPosition;
    track.style.backgroundRepeat = style.backgroundRepeat;
    return style;
}

export function renderTimingErrors(deps = {}) {
    const {
        errorPanel,
        errors = [],
        escapeHtml = (value) => String(value ?? "")
    } = deps;
    if (!errorPanel) return 0;
    if (!errors.length) {
        errorPanel.style.display = "none";
        errorPanel.innerHTML = "";
        return 0;
    }
    errorPanel.style.display = "block";
    errorPanel.innerHTML = "<strong>エラー:</strong><ul>" + errors.map((error) => `<li>${escapeHtml(error)}</li>`).join("") + "</ul>";
    return errors.length;
}

export function renderTimingGrid(doc = document, deps = {}) {
    const {
        timingGrid,
        state,
        buildTimingGridViewState = () => ({ totalSec: 1, trackWidth: 100, rulerMarks: [], axisRows: [] }),
        escapeHtml = (value) => String(value ?? ""),
        applyTrackGridBackground = () => {},
        renderAxisCurveTrack = () => {},
        renderSubprocessTimingRow = () => {},
        buildDetailContent = () => null,
        renderSpeedChart = () => {},
        addMotion = () => {},
        removeAxis = () => {},
        updateAllFlowNodeLabels = () => {}
    } = deps;
    if (!timingGrid) return null;

    timingGrid.innerHTML = "";
    const pxPerSec = state.pxPerSec;
    const gridState = buildTimingGridViewState(pxPerSec);
    const { totalSec, trackWidth } = gridState;

    const ruler = doc.createElement("div");
    ruler.className = "ruler";
    const rulerLabel = doc.createElement("div");
    rulerLabel.className = "axis-side";
    rulerLabel.textContent = "時間軸 (秒)";
    ruler.appendChild(rulerLabel);
    const rulerTrack = doc.createElement("div");
    rulerTrack.className = "axis-track";
    rulerTrack.style.minWidth = `${trackWidth}px`;
    applyTrackGridBackground(rulerTrack, pxPerSec);
    const scaleControl = doc.createElement("label");
    scaleControl.className = "ruler-scale-control";
    scaleControl.innerHTML = `目盛り <input type="number" id="pxPerSec" value="${escapeHtml(String(state.pxPerSec))}" min="20" max="600" step="10">`;
    rulerTrack.appendChild(scaleControl);
    for (const mark of gridState.rulerMarks) {
        const label = doc.createElement("div");
        label.className = "ruler-label";
        label.style.left = `${mark.leftPx}px`;
        label.textContent = mark.label;
        rulerTrack.appendChild(label);
    }
    ruler.appendChild(rulerTrack);
    timingGrid.appendChild(ruler);

    for (const rowState of gridState.axisRows) {
        const { axis } = rowState;
        const row = doc.createElement("div");
        row.className = "axis-row";

        const side = doc.createElement("div");
        side.className = `axis-side${rowState.isSelected ? " selected" : ""}`;
        side.addEventListener("click", (event) => {
            const targetTagName = event?.target?.tagName;
            if (targetTagName === "INPUT" || targetTagName === "BUTTON" || targetTagName === "SELECT") return;
            state.selectedAxisId = axis.id;
            state.selectedMotionId = null;
            renderTimingGrid(doc, deps);
            renderSpeedChart();
        });

        const nameInput = doc.createElement("input");
        nameInput.type = "text";
        nameInput.className = "axis-name-input";
        nameInput.value = axis.name;
        nameInput.addEventListener("input", () => {
            axis.name = nameInput.value;
            updateAllFlowNodeLabels(axis.id);
        });
        side.appendChild(nameInput);

        const meta = doc.createElement("div");
        meta.className = "axis-meta";
        meta.innerHTML = `
            <div>軸最高速度: ${rowState.axisMaxSpeedText}</div>
        `;
        side.appendChild(meta);

        const actions = doc.createElement("div");
        actions.className = "axis-actions";
        const addBtn = doc.createElement("button");
        addBtn.textContent = "＋動作";
        addBtn.addEventListener("click", () => addMotion(axis.id));
        const delBtn = doc.createElement("button");
        delBtn.textContent = "軸削除";
        delBtn.className = "danger";
        delBtn.addEventListener("click", () => removeAxis(axis.id));
        actions.appendChild(addBtn);
        actions.appendChild(delBtn);
        side.appendChild(actions);
        row.appendChild(side);

        const track = doc.createElement("div");
        track.className = "axis-track";
        track.style.minWidth = `${trackWidth}px`;
        renderAxisCurveTrack(axis, track, totalSec, pxPerSec, trackWidth);
        row.appendChild(track);
        timingGrid.appendChild(row);

        if (rowState.showDetail) {
            const detail = doc.createElement("div");
            detail.className = "detail-section";
            const content = buildDetailContent(axis);
            if (content) detail.appendChild(content);
            timingGrid.appendChild(detail);
        }
    }

    renderSubprocessTimingRow(totalSec, pxPerSec, trackWidth);
    return gridState;
}

export function renderSubprocessTimingRow(doc = document, deps = {}) {
    const {
        timingGrid,
        state,
        trackHeight = 100,
        totalSec = 1,
        pxPerSec = 100,
        trackWidth = 100,
        buildSubprocessTimingRowState = () => [],
        applyTrackGridBackground = () => {},
        renderFlowNodeEditor = () => {},
        renderSpeedChart = () => {}
    } = deps;
    const items = buildSubprocessTimingRowState(pxPerSec);
    if (!items.length || !timingGrid) return [];

    const row = doc.createElement("div");
    row.className = "axis-row";

    const side = doc.createElement("div");
    side.className = "axis-side";
    side.innerHTML = `<div style="font-weight:700;">サブプロセス</div><div class="axis-meta"><div>固定秒数工程</div></div>`;
    row.appendChild(side);

    const track = doc.createElement("div");
    track.className = "axis-track";
    track.style.minWidth = `${trackWidth}px`;
    applyTrackGridBackground(track, pxPerSec);

    const svg = doc.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "curve-svg");
    svg.setAttribute("width", trackWidth);
    svg.setAttribute("height", trackHeight);

    for (const item of items) {
        const rect = doc.createElementNS(SVG_NS, "rect");
        rect.setAttribute("x", item.x);
        rect.setAttribute("y", 18);
        rect.setAttribute("width", item.width);
        rect.setAttribute("height", trackHeight - 36);
        rect.setAttribute("rx", 6);
        rect.setAttribute("fill", "#f6ad55");
        rect.setAttribute("fill-opacity", "0.35");
        rect.setAttribute("stroke", "#c05621");
        rect.setAttribute("stroke-width", "1.5");
        rect.style.cursor = "pointer";
        rect.addEventListener("click", (event) => {
            event.stopPropagation();
            state.selectedFlowNodeId = item.nodeId;
            renderFlowNodeEditor();
            renderSpeedChart();
        });
        svg.appendChild(rect);

        const label = doc.createElementNS(SVG_NS, "text");
        label.setAttribute("class", "motion-label");
        label.setAttribute("x", item.x + 4);
        label.setAttribute("y", 14);
        label.textContent = item.label;
        svg.appendChild(label);

        const timeLabel = doc.createElementNS(SVG_NS, "text");
        timeLabel.setAttribute("class", "motion-label-time");
        timeLabel.setAttribute("x", item.x + 4);
        timeLabel.setAttribute("y", trackHeight - 3);
        timeLabel.textContent = item.timeLabel;
        svg.appendChild(timeLabel);
    }

    track.appendChild(svg);
    row.appendChild(track);
    timingGrid.appendChild(row);
    return items;
}

export function renderAxisCurveTrack(doc = document, deps = {}) {
    const {
        axis,
        track,
        state,
        pxPerSec = 100,
        trackWidth = 100,
        trackHeight = 100,
        applyTrackGridBackground = () => {},
        buildAxisCurveTrackState = () => ({ regions: [], totalDist: 0 }),
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        distLabel = () => "0",
        formatDistance = (currentAxis, value) => `${value}`
    } = deps;
    if (!track || !axis) return null;

    track.style.minWidth = `${trackWidth}px`;
    applyTrackGridBackground(track, pxPerSec);
    const curveState = buildAxisCurveTrackState(axis, pxPerSec);

    const svg = doc.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "curve-svg");
    svg.setAttribute("width", trackWidth);
    svg.setAttribute("height", trackHeight);

    for (const item of curveState.regions) {
        const { motion, times, err } = item;
        const x = item.x;
        const wMotion = item.motionWidth;
        const wWait = item.waitWidth;
        if (wMotion > 0) {
            const rect = doc.createElementNS(SVG_NS, "rect");
            rect.setAttribute("class", `motion-region${motion.id === state.selectedMotionId ? " selected" : ""}${err ? " error-region" : ""}`);
            rect.setAttribute("x", x);
            rect.setAttribute("y", 0);
            rect.setAttribute("width", wMotion);
            rect.setAttribute("height", trackHeight);
            rect.addEventListener("click", (event) => {
                event.stopPropagation();
                state.selectedAxisId = axis.id;
                state.selectedMotionId = motion.id;
                renderTimingGrid();
                renderSpeedChart();
            });
            const titleEl = doc.createElementNS(SVG_NS, "title");
            titleEl.textContent = err
                ? `${motion.name}\n${err}`
                : `${motion.name}\n${times.startSec.toFixed(3)}→${times.endSec.toFixed(3)}s\n移動距離: ${distLabel(axis, motion.distance)}`;
            rect.appendChild(titleEl);
            svg.appendChild(rect);
        }
        if (wWait > 0) {
            const waitRect = doc.createElementNS(SVG_NS, "rect");
            waitRect.setAttribute("class", "wait-region");
            waitRect.setAttribute("x", x + wMotion);
            waitRect.setAttribute("y", 0);
            waitRect.setAttribute("width", wWait);
            waitRect.setAttribute("height", trackHeight);
            svg.appendChild(waitRect);
        }

        const label = doc.createElementNS(SVG_NS, "text");
        label.setAttribute("class", "motion-label");
        label.setAttribute("x", x + 4);
        label.setAttribute("y", 12);
        label.textContent = item.label;
        svg.appendChild(label);

        const timeLabel = doc.createElementNS(SVG_NS, "text");
        timeLabel.setAttribute("class", "motion-label-time");
        timeLabel.setAttribute("x", x + 4);
        timeLabel.setAttribute("y", trackHeight - 3);
        timeLabel.textContent = item.timeLabel;
        svg.appendChild(timeLabel);
    }

    if (curveState.polylinePoints) {
        const poly = doc.createElementNS(SVG_NS, "polyline");
        poly.setAttribute("class", `pos-curve${state.selectedAxisId === axis.id ? " selected" : ""}`);
        poly.setAttribute("points", curveState.polylinePoints);
        svg.appendChild(poly);
    }

    track.appendChild(svg);

    if (curveState.totalDist > 0) {
        const yMax = doc.createElement("div");
        yMax.className = "y-scale";
        yMax.style.top = `${curveState.yScaleTopPx}px`;
        yMax.textContent = formatDistance(axis, curveState.totalDist);
        track.appendChild(yMax);

        const y0 = doc.createElement("div");
        y0.className = "y-scale";
        y0.style.top = `${curveState.yScaleBottomPx}px`;
        y0.textContent = "0";
        track.appendChild(y0);
    }

    return curveState;
}

export function buildAxisConfigSection(doc = document, deps = {}) {
    const {
        axis,
        speedUnitDetail = "",
        buildActuatorTypeOptions = () => "",
        normalizeMotorControlType = (value) => value ?? "",
        isFinitePositive = () => false,
        rebuildAll = () => {},
        alertFn = () => {}
    } = deps;
    if (!axis) return null;

    const cfg = doc.createElement("div");
    cfg.className = "axis-config";
    const motorControlType = normalizeMotorControlType(axis.motorControlType, axis.actuatorType);
    cfg.innerHTML = `
        <label>アクチュエータ種別: <select data-axis-field="actuatorType">
            ${buildActuatorTypeOptions(axis.actuatorType)}
        </select></label>
        <label>モータ制御種別: <select data-axis-field="motorControlType">
            <option value="" ${motorControlType === "" ? "selected" : ""}>—</option>
            <option value="speedControl" ${motorControlType === "speedControl" ? "selected" : ""}>速度指定モータ</option>
            <option value="pulseCommand" ${motorControlType === "pulseCommand" ? "selected" : ""}>パルスモータ（コマンド入力）</option>
            <option value="pulseTrain" ${motorControlType === "pulseTrain" ? "selected" : ""}>パルスモータ（パルス入力）</option>
            <option value="servo" ${motorControlType === "servo" ? "selected" : ""}>サーボモータ</option>
            <option value="inverter" ${motorControlType === "inverter" ? "selected" : ""}>インバータ</option>
        </select></label>
        <label>動作種別: <select data-axis-field="motionType">
            <option value="linear" ${axis.motionType === "linear" ? "selected" : ""}>直線</option>
            <option value="rotary" ${axis.motionType === "rotary" ? "selected" : ""}>回転</option>
        </select></label>
        <label>軸最高速度上限: <input type="number" data-axis-field="axisMaxSpeed" min="0" step="1" value="${axis.axisMaxSpeed ?? ""}"> ${speedUnitDetail}</label>
        <button id="applyAxisMaxBtn" type="button">この上限をこの軸の動作に反映</button>
    `;
    cfg.querySelectorAll("input,select").forEach((el) => {
        el.addEventListener("change", () => {
            const value = el.tagName === "SELECT" ? el.value : (el.value === "" ? null : parseFloat(el.value));
            axis[el.dataset.axisField] = (typeof value === "string" || value == null) ? value : (isFinite(value) ? value : null);
            rebuildAll();
        });
    });
    cfg.querySelector("#applyAxisMaxBtn")?.addEventListener("click", () => {
        if (!isFinitePositive(axis.axisMaxSpeed)) {
            alertFn("最高速度を入力してください");
            return;
        }
        for (const motion of axis.motions) motion.maxSpeed = axis.axisMaxSpeed;
        rebuildAll();
    });
    return cfg;
}

export function buildCylinderPresetSection(doc = document, deps = {}) {
    const {
        axis,
        state,
        escapeHtml = (value) => String(value ?? ""),
        detectCylinderPreset = () => "",
        applyCylinderPreset = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        rebuildAll = () => {}
    } = deps;
    if (!axis || axis.motionType !== "linear" || axis.motions.length === 0) return null;

    const selectedMotion = axis.motions.find((motion) => motion.id === state.selectedMotionId) || axis.motions[0];
    const presetBox = doc.createElement("div");
    presetBox.className = "axis-config";
    const selectedPreset = detectCylinderPreset(axis, selectedMotion);
    presetBox.innerHTML = `
        <label>対象動作: <select data-role="presetTarget">
            ${axis.motions.map((motion) => `<option value="${motion.id}" ${motion.id === selectedMotion.id ? "selected" : ""}>${escapeHtml(motion.name)}</option>`).join("")}
        </select></label>
        <label>動作プリセット: <select data-role="cylinderPreset">
            <option value="">カスタム設定</option>
            <option value="slow" ${selectedPreset === "slow" ? "selected" : ""}>遅い</option>
            <option value="normal" ${selectedPreset === "normal" ? "selected" : ""}>普通</option>
            <option value="fast" ${selectedPreset === "fast" ? "selected" : ""}>速い</option>
        </select></label>
    `;
    presetBox.querySelector('[data-role="presetTarget"]')?.addEventListener("change", (event) => {
        state.selectedMotionId = event.target.value || null;
        renderTimingGrid();
        renderSpeedChart();
    });
    presetBox.querySelector('[data-role="cylinderPreset"]')?.addEventListener("change", (event) => {
        if (!event.target.value) return;
        applyCylinderPreset(selectedMotion, event.target.value);
        state.selectedMotionId = selectedMotion.id;
        rebuildAll();
    });
    return presetBox;
}

export function buildMotionDetailTable(doc = document, deps = {}) {
    const {
        axis,
        speedUnitDetail = "",
        distanceUnitDetail = "",
        accelUnitDetail = "",
        buildMotionDetailRow = (...args) => defaultBuildMotionDetailRow(...args)
    } = deps;
    if (!axis) return null;

    const table = doc.createElement("table");
    table.innerHTML = `
        <thead>
            <tr>
                <th>動作名</th>
                <th>開始条件</th>
                <th>参照元</th>
                <th>オフセット秒</th>
                <th>距離 (${distanceUnitDetail})</th>
                <th>加速度 (${accelUnitDetail})</th>
                <th>減速度 (${accelUnitDetail})</th>
                <th>最高速度 (${speedUnitDetail})</th>
                <th>待ち時間 (s)</th>
                <th>コメント</th>
                <th>所要 (s)</th>
                <th>開始 (s)</th>
                <th>終了 (s)</th>
                <th>算出最高 (${speedUnitDetail})</th>
                <th></th>
            </tr>
        </thead>
        <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody");
    for (const motion of axis.motions) {
        const tr = buildMotionDetailRow(doc, { ...deps, axis, motion });
        if (tr) tbody?.appendChild(tr);
    }
    return table;
}

export function buildMotionDetailRow(doc = document, deps = {}) {
    const {
        axis,
        motion,
        state,
        escapeHtml = (value) => String(value ?? ""),
        buildMotionDetailRowState = () => ({
            times: null,
            err: "",
            startMode: "absolute",
            sourceMotionOptions: [],
            adjustedMaxText: ""
        }),
        onMotionFieldChange = () => {},
        removeMotion = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {}
    } = deps;
    if (!axis || !motion || !state) return null;

    const tr = doc.createElement("tr");
    const rowState = buildMotionDetailRowState(axis, motion);
    const { times, err, startMode, sourceMotionOptions, adjustedMaxText } = rowState;
    const refOptions = sourceMotionOptions
        .map((option) => `<option value="${option.value}" ${option.selected ? "selected" : ""}>${option.label}</option>`)
        .join("");

    tr.innerHTML = `
        <td><input type="text" data-mfield="name" value="${escapeHtml(motion.name)}"></td>
        <td>
            <select data-mfield="startMode">
                <option value="absolute" ${startMode === "absolute" ? "selected" : ""}>絶対時刻</option>
                <option value="ref" ${startMode === "ref" ? "selected" : ""}>参照終了+α</option>
            </select>
            ${startMode === "absolute" ? `<input type="number" step="0.01" data-mfield="absoluteStartSec" value="${motion.absoluteStartSec ?? 0}" style="width:60px;margin-left:4px;">s` : ""}
            <div style="margin-top:4px;">
                <input type="text" data-mfield="startConditionText" value="${escapeHtml(motion.startConditionText || "")}" placeholder="条件例: センサーON">
            </div>
        </td>
        <td>${startMode === "ref" ? `<select data-mfield="sourceMotionId"><option value="">(未選択)</option>${refOptions}</select>` : "—"}</td>
        <td>${startMode === "ref" ? `<input type="number" step="0.01" data-mfield="offsetSec" value="${motion.dependency?.offsetSec ?? 0}">` : "—"}</td>
        <td><input type="number" step="0.1" data-mfield="distance" value="${motion.distance ?? ""}"></td>
        <td><input type="number" min="0" step="0.01" data-mfield="accel" value="${motion.accel ?? ""}"></td>
        <td><input type="number" min="0" step="0.01" data-mfield="decel" value="${motion.decel ?? ""}"></td>
        <td><input type="number" min="0" step="1" data-mfield="maxSpeed" value="${motion.maxSpeed ?? ""}"></td>
        <td><input type="number" min="0" step="0.01" data-mfield="waitTime" value="${motion.waitTime ?? 0}"></td>
        <td><input type="text" data-mfield="comment" value="${escapeHtml(motion.comment || "")}"></td>
        <td class="computed">${times ? times.cycleTime.toFixed(3) : "—"}</td>
        <td class="computed">${times ? times.startSec.toFixed(3) : "—"}</td>
        <td class="computed">${times ? times.endSec.toFixed(3) : "—"}</td>
        <td class="computed">${adjustedMaxText || "—"}</td>
        <td class="row-actions"><button data-action="del">削除</button></td>
    `;
    if (err) {
        tr.style.background = "#fff5f5";
        tr.title = err;
    }
    if (motion.id === state.selectedMotionId) tr.style.outline = "2px solid #f6e05e";

    tr.addEventListener("click", (event) => {
        const targetTagName = event?.target?.tagName;
        if (targetTagName !== "INPUT" && targetTagName !== "SELECT" && targetTagName !== "BUTTON") {
            state.selectedMotionId = motion.id;
            renderTimingGrid();
            renderSpeedChart();
        }
    });

    tr.querySelectorAll("[data-mfield]").forEach((el) => {
        el.addEventListener("change", () => onMotionFieldChange(axis, motion, el));
        if (el.tagName === "INPUT" && el.type !== "number") {
            el.addEventListener("input", () => onMotionFieldChange(axis, motion, el));
        }
    });
    tr.querySelector("[data-action='del']")?.addEventListener("click", () => removeMotion(motion.id));
    return tr;
}

function defaultBuildMotionDetailRow(...args) {
    return buildMotionDetailRow(...args);
}

export function buildDetailContent(doc = document, deps = {}) {
    const {
        axis,
        state,
        escapeHtml = (value) => String(value ?? ""),
        getAxisSpeedUnit = () => "",
        getAxisDistanceUnit = () => "",
        getAxisAccelUnit = () => "",
        buildAxisConfigSection = () => null,
        buildCylinderPresetSection = () => null,
        buildMotionDetailTable = () => null
    } = deps;
    if (!axis || !state) return null;

    const wrapper = doc.createElement("details");
    wrapper.className = "detail-collapsible";
    if (state.openDetailAxes?.has(axis.id)) wrapper.open = true;
    wrapper.addEventListener("toggle", () => {
        if (!state.openDetailAxes) return;
        if (wrapper.open) state.openDetailAxes.add(axis.id);
        else state.openDetailAxes.delete(axis.id);
    });

    const summary = doc.createElement("summary");
    summary.innerHTML = `<strong>詳細設定: ${escapeHtml(axis.name)}</strong> <span style="color:#666;font-size:0.85em;">（クリックで開閉）</span>`;
    wrapper.appendChild(summary);

    const speedUnitDetail = getAxisSpeedUnit(axis);
    const distanceUnitDetail = getAxisDistanceUnit(axis);
    const accelUnitDetail = getAxisAccelUnit(axis);

    const cfg = buildAxisConfigSection(axis, speedUnitDetail);
    if (cfg) wrapper.appendChild(cfg);

    const presetBox = buildCylinderPresetSection(axis);
    if (presetBox) wrapper.appendChild(presetBox);

    const table = buildMotionDetailTable(axis, speedUnitDetail, distanceUnitDetail, accelUnitDetail);
    if (table) wrapper.appendChild(table);

    return wrapper;
}

export function handleMotionFieldChange(deps = {}) {
    const {
        axis,
        motion,
        element,
        updateAllFlowNodeLabels = () => {},
        recomputeAll = () => {},
        renderErrors = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        rebuildAll = () => {},
        removeFlowConnectionsToMotion = () => {},
        addFlowConnection = () => {}
    } = deps;
    if (!motion || !element?.dataset?.mfield) return false;

    const field = element.dataset.mfield;
    const value = element.value;
    if (field === "name" || field === "comment" || field === "startConditionText") {
        motion[field] = value;
        updateAllFlowNodeLabels();
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        return true;
    }
    if (field === "startMode") {
        if (value === "absolute") {
            motion.dependency = null;
            if (motion.absoluteStartSec == null) motion.absoluteStartSec = 0;
            removeFlowConnectionsToMotion(motion.id);
        } else {
            motion.absoluteStartSec = null;
            if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        }
        rebuildAll();
        return true;
    }
    if (field === "sourceMotionId") {
        if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        motion.dependency.sourceMotionId = value;
        removeFlowConnectionsToMotion(motion.id);
        if (value) addFlowConnection(value, motion.id);
        rebuildAll();
        return true;
    }
    if (field === "offsetSec") {
        if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        motion.dependency.offsetSec = parseFloat(value) || 0;
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        renderSpeedChart();
        return true;
    }
    if (field === "absoluteStartSec") {
        motion.absoluteStartSec = parseFloat(value) || 0;
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        renderSpeedChart();
        return true;
    }

    const num = value === "" ? null : parseFloat(value);
    motion[field] = (num == null || !isFinite(num)) ? null : num;
    recomputeAll();
    renderErrors();
    renderTimingGrid();
    renderSpeedChart();
    updateAllFlowNodeLabels();
    return true;
}
